import { Effect, Option } from 'effect';

import { CategoryListing } from '@vs-shop/engine/capability';
import { capabilityLayer } from '@vs-shop/engine/capability/capabilityLayer';
import type { TaxonPageRequest } from '@vs-shop/engine/capability/categoryListing/CategoryListing';
import { CategoryListingError } from '@vs-shop/engine/capability/categoryListing/errors';
import type { TaxonNode } from '@vs-shop/engine/capability/categoryListing/taxonNodeSchema';
import type { EffectSuccess } from '@vs-shop/engine/data/util/effectTypes';

import { requestTaxonExpandByCode } from '@vs-shop/engine/request/backendApi/category/getTaxonExpandedByCode';
import { getProductsByTaxon } from '../bind/product';
import { toProductPage } from './productListing';

/**
 * rcprofi's answer to {@link CategoryListing}, over its existing bind exports.
 *
 * `taxon` reads `/taxons/{code}/expand` and `products` reads `/products`
 * filtered by that taxon. They are two backend calls because they are two
 * reads, which is exactly the split the port declares — the loader that runs
 * them concurrently and decides branch-versus-leaf afterwards is the tenant's,
 * and this provider states no opinion about it.
 *
 * `inStock: true` is applied inside `getProductsByTaxon` and never reaches the
 * port. A backend filter is a fact about rcprofi's catalogue endpoint, not
 * about what a category listing means, so it stays where the request is built.
 */

type TaxonExpanded = EffectSuccess<ReturnType<typeof requestTaxonExpandByCode>>;
type TaxonExpandedChild = NonNullable<TaxonExpanded['children']>[number];

/**
 * The fields a taxon contributes to the curated shape.
 *
 * A child taxon carries the same five scalars and no `children` key of its
 * own, so the two mappings differ only in what they can nest — which is why
 * this is a function over the shared fields rather than a duplicated literal.
 *
 * `attributes` is `None`: this provider does not map facets through the
 * port, and no page reads this field through it. rcprofi sends the facets
 * on this same response, and its category page reads them through its own
 * reader on the raw body, not through this port — that stays out of scope
 * here.
 *
 * The price bounds, the articles and the trail are likewise not mapped
 * through this port and stay empty for the same reason `attributes` used to
 * be — re-homing those consumers is closeout row C2's work, not this
 * provider's.
 *
 * `image` is `None` for the same reason: rcprofi's category page reads a
 * taxon's images from its own raw response, not through this port.
 */
const taxonFields = (taxon: TaxonExpanded | TaxonExpandedChild) => ({
  code: taxon.code,
  name: Option.fromNullishOr(taxon.name),
  listName: Option.fromNullishOr(taxon.nameInTaxonList),
  href: Option.fromNullishOr(taxon.routerUrl),
  description: Option.fromNullishOr(taxon.description),
  image: Option.none(),
  totalProducts: Option.fromNullishOr(taxon.totalProducts),
  attributes: Option.none(),
  priceRange: Option.none(),
  articles: [],
  ancestors: [],
});

/**
 * A child taxon, curated.
 *
 * `children` is empty because rcprofi's expand endpoint inlines one level and
 * a child carries no nested children. The port cannot tell an unexpanded child
 * from a childless one, and neither can this adapter, so it answers the shape
 * it actually has rather than guessing.
 */
const toTaxonChild = (taxon: TaxonExpandedChild): TaxonNode => ({
  ...taxonFields(taxon),
  children: [],
});

/**
 * One taxon, curated.
 *
 * rcprofi's expanded taxon also carries `routerUrl`, `breadcrumbs`, `banners`,
 * `articles`, `brands`, `attributes`, `priceRange`, `images`, `slug`,
 * `metaTitle`, `metaDescription`, `listing` and `visibility`. None enters the
 * curated shape: each is either rcprofi-only or read by no live page through
 * this port. The category page reads several of them today, and it keeps
 * reading them through its own reader on the raw response — re-homing those
 * consumers is closeout row C2's work, not this provider's.
 */
const toTaxonNode = (taxon: TaxonExpanded): TaxonNode => ({
  ...taxonFields(taxon),
  children: (taxon.children ?? []).map(toTaxonChild),
});

const taxon = (code: string) =>
  requestTaxonExpandByCode(code).pipe(
    Effect.map(toTaxonNode),
    Effect.mapError(CategoryListingError.fromRequestFailure('taxon', code))
  );

const products = (request: TaxonPageRequest) =>
  getProductsByTaxon({
    taxonCode: request.taxonCode,
    page: request.page,
    itemsPerPage: request.itemsPerPage,
  }).pipe(
    Effect.map((response) => toProductPage(response, request)),
    Effect.mapError(CategoryListingError.fromRequestFailure('products', request.taxonCode))
  );

export const CategoryListingLive = capabilityLayer(CategoryListing, {
  taxon,
  products,
});
